import React, { useState, useContext, useEffect } from 'react';
import { firestore, auth } from '../../firebase/config';
import { BookingContext } from '../../context/BookingContext';
import { AuthContext } from '../../context/AuthContext';
import emailService from '../../services/emailService';
import './ClientForm.css';

const ClientForm = () => {
  const { currentUser, userProfile } = useContext(AuthContext);
  const [invitMode, setInvitMode ] = useState(false);
  const { bookingData, setCurrentStep, updateBookingData } = useContext(BookingContext);
  const [formData, setFormData] = useState({
    name: '',
    firstname: '',
    email: '',
    phone: '',
    notes: '',
    // Nouveau champ pour l'adresse en cas de visite à domicile
    address: {
      street: '',
      zipCode: '',
      city: '',
      additionalInfo: ''
    }
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [globalError, setGlobalError] = useState(null);

  // Préremplir le formulaire avec les données de l'utilisateur connecté
  useEffect(() => {
    if (currentUser && userProfile) {
      setFormData(prevData => ({
        ...prevData,
        name: userProfile.name || '',
        firstname: userProfile.firstname || '',
        email: currentUser.email || '',
        phone: userProfile.phone || '',
        address: userProfile.address || {
          street: '',
          zipCode: '',
          city: '',
          additionalInfo: ''
        }
      }));
    }
  }, [currentUser, userProfile]);

  // Gérer les changements dans le formulaire
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Gérer les champs d'adresse
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData({
        ...formData,
        address: {
          ...formData.address,
          [addressField]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : value
      });
    }

    // Supprimer l'erreur lorsque l'utilisateur corrige le champ
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };

  // Déterminer si c'est un rendez-vous à domicile
  const isHomeVisit = () => {
    return bookingData.consultationMode === 'home';
  };

  // Déterminer si c'est un rendez-vous à distance
  const isRemoteVisit = () => {
    return bookingData.consultationMode === 'remote';
  };

  // Calculer le prix total avec les frais de déplacement si applicable
  const calculateTotalPrice = () => {
    let totalPrice = bookingData.price || 0;
    
    // Ajouter les frais de déplacement pour les visites à domicile
    if (isHomeVisit() && bookingData.homeTransportFee) {
      totalPrice += bookingData.homeTransportFee;
    }
    
    return totalPrice;
  };

  // Valider le formulaire
  const validateForm = () => {
    const newErrors = {};

    // Validation du nom
    if (!formData.name.trim()) {
      newErrors.name = 'Le nom est requis';
    }

    // Validation du prénom
    if (!formData.firstname.trim()) {
      newErrors.firstname = 'Le prénom est requis';
    }

    // Validation de l'email
    if (!formData.email.trim()) {
      newErrors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Veuillez entrer un email valide';
    }

    // Validation du téléphone
    if (!formData.phone.trim()) {
      newErrors.phone = 'Le numéro de téléphone est requis';
    } else if (!/^(\+33|0)[1-9](\d{2}){4}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Veuillez entrer un numéro de téléphone valide';
    }

    // Validation de l'adresse pour les visites à domicile
    if (isHomeVisit()) {
      if (!formData.address.street.trim()) {
        newErrors['address.street'] = 'L\'adresse est requise pour une visite à domicile';
      }
      if (!formData.address.zipCode.trim()) {
        newErrors['address.zipCode'] = 'Le code postal est requis';
      }
      if (!formData.address.city.trim()) {
        newErrors['address.city'] = 'La ville est requise';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Formater le numéro de téléphone
  const formatPhone = (phone) => {
    // Supprimer tous les caractères non numériques
    const cleaned = phone.replace(/\D/g, '');

    // Format français (+33 ou 0)
    if (cleaned.startsWith('33') && cleaned.length === 11) {
      return `+33 ${cleaned.substring(2, 3)} ${cleaned.substring(3, 5)} ${cleaned.substring(5, 7)} ${cleaned.substring(7, 9)} ${cleaned.substring(9, 11)}`;
    } else if (cleaned.startsWith('0') && cleaned.length === 10) {
      return `${cleaned.substring(0, 2)} ${cleaned.substring(2, 4)} ${cleaned.substring(4, 6)} ${cleaned.substring(6, 8)} ${cleaned.substring(8, 10)}`;
    }

    // Si le format n'est pas reconnu, renvoyer tel quel
    return phone;
  };

  // Formater l'adresse pour l'affichage
  const formatAddress = (address) => {
    if (!address || (!address.street && !address.city)) return '';
    
    let formattedAddress = address.street || '';
    if (address.zipCode || address.city) {
      formattedAddress += formattedAddress ? `, ${address.zipCode} ${address.city}`.trim() : `${address.zipCode} ${address.city}`.trim();
    }
    if (address.additionalInfo) {
      formattedAddress += formattedAddress ? ` (${address.additionalInfo})` : address.additionalInfo;
    }
    
    return formattedAddress;
  };

  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    setGlobalError(null);
  
    if (!validateForm()) {
      return;
    }
  
    if (!bookingData.practitioner || !bookingData.service || !bookingData.date || !bookingData.time) {
      setGlobalError('Informations de réservation incomplètes. Veuillez recommencer.');
      return;
    }
  
    setIsSubmitting(true);
  
    try {
      let practitionerEmail = '';
      try {
        const practitionerDoc = await firestore
          .collection('practitioners')
          .doc(bookingData.practitioner.id)
          .get();
        
        if (practitionerDoc.exists) {
          const practitionerData = practitionerDoc.data();
          practitionerEmail = practitionerData.email || '';
          console.log('Email du praticien récupéré:', practitionerEmail);
        }
      } catch (error) {
        console.error('Erreur lors de la récupération des détails du praticien:', error);
      }
  
      // Formater le numéro de téléphone
      const formattedPhone = formatPhone(formData.phone);
      
      // Calculer le prix total
      const totalPrice = calculateTotalPrice();
  
      // Préparer les données du rendez-vous
      const appointmentData = {
        practitionerId: bookingData.practitioner.id,
        practitionerName: bookingData.practitioner.name,
        practitionerEmail: practitionerEmail,
        serviceId: bookingData.service,
        serviceTitle: bookingData.serviceTitle,
        serviceNotes: bookingData.notes,
        date: formatDateForStorage(bookingData.date),
        formattedDate: bookingData.formattedDate,
        time: bookingData.time,
        duration: bookingData.duration,
        restTime: bookingData.restTime || 0,
        price: totalPrice, // Prix total incluant les frais de déplacement
        basePrice: bookingData.price, // Prix de base du service
        name: formData.firstname + ' ' + formData.name.toUpperCase(),
        email: formData.email,
        phone: formattedPhone,
        notes: formData.notes,
        status: 'confirmed',
        createdAt: new Date(),
        userId: currentUser ? currentUser.uid : null,
        reminderSent: false,
        
        // Mode de consultation
        consultationMode: bookingData.consultationMode,
        isRemote: isRemoteVisit(),
        isHomeVisit: isHomeVisit(),
        
        // Adresse pour les visites à domicile
        clientAddress: isHomeVisit() ? {
          street: formData.address.street,
          zipCode: formData.address.zipCode,
          city: formData.address.city,
          additionalInfo: formData.address.additionalInfo,
          formattedAddress: formatAddress(formData.address)
        } : null,
        
        // Frais de déplacement pour les visites à domicile
        homeTransportFee: isHomeVisit() ? (bookingData.homeTransportFee || 0) : 0,
        
        // Structure pour les rendez-vous à distance
        remoteDetails: isRemoteVisit() ? {
          linkSent: false,
          platform: 'meet',
          meetLink: null,
          meetEventId: null
        } : null
      };
  
      // Enregistrer le rendez-vous dans Firestore
      const appointmentRef = await firestore.collection('appointments').add(appointmentData);
      
      // Récupérer l'ID du document créé
      const appointmentWithId = {
        ...appointmentData,
        id: appointmentRef.id,
        practitioner: bookingData.practitioner
      };
  
      // Envoyer un email de confirmation au client
      try {
        emailService.sendConfirmationEmail(appointmentWithId, 'client');
        console.log('Email de confirmation envoyé au client');
      } catch (emailError) {
        console.error('Erreur lors de l\'envoi de l\'email au client:', emailError);
      }
  
      // Envoyer un email de notification au praticien
      try {
        emailService.sendConfirmationEmail(appointmentWithId, 'practitioner');
        console.log('Email de confirmation envoyé au praticien');
      } catch (emailError) {
        console.error('Erreur lors de l\'envoi de l\'email au praticien:', emailError);
      }
  
      // Mettre à jour les données de réservation avec les informations du client
      updateBookingData({
        name: formData.firstname + ' ' + formData.name.toUpperCase(),
        email: formData.email,
        phone: formattedPhone,
        notes: formData.notes,
        clientAddress: isHomeVisit() ? formData.address : null,
        totalPrice: totalPrice
      });
  
      // Passer à l'étape de confirmation
      setCurrentStep(6);
    } catch (error) {
      console.error('Erreur lors de la réservation:', error);
      setGlobalError('Une erreur est survenue lors de la réservation. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Revenir à l'étape précédente
  const handleBack = () => {
    setCurrentStep(4);
  };

  const handleHideAuthModal = () => {
    setInvitMode(true);
  };

  // Ouvrir la modal d'authentification
  const handleAuthModal = () => {
    if (typeof window.openAuthModal === 'function') {
      window.openAuthModal();
    } else {
      console.warn("La modal d'authentification n'est pas disponible.");
      setGlobalError("Impossible d'ouvrir la modal d'authentification. Veuillez rafraîchir la page et réessayer.");
    }
  };

  // Si l'utilisateur n'est pas connecté, afficher un écran demandant de se connecter
  if (!currentUser && !invitMode) {
    return (
      <div className="client-form-container">
        <h2>Authentification requise</h2>
        
        <div className="auth-required-box">
          <div className="auth-icon">🔒</div>
          <h3>Veuillez vous connecter pour continuer</h3>
          <p>
            Pour finaliser votre réservation, vous devez être connecté à votre compte. 
            Si vous n'avez pas encore de compte, vous pourrez en créer un en quelques instants.
          </p>
          <div className="auth-benefits">
            <h4>Avantages de la création d'un compte :</h4>
            <ul>
              <li>Accès à l'historique de vos rendez-vous</li>
              <li>Modification ou annulation facile de vos réservations</li>
              <li>Réservation plus rapide pour vos prochains rendez-vous</li>
            </ul>
          </div>
          <div className="auth-buttons">
            <button className="btn btn-primary" onClick={handleAuthModal}>
              Se connecter / Créer un compte
            </button>
            <button className="btn btn-outline" onClick={handleHideAuthModal}>
              Continuer sans compte
            </button>
            <button className="btn btn-outline" onClick={handleBack}>
              Retour
            </button>
          </div>
          <div className="auth-info">
            <p>Vos données sont protégées conformément à notre politique de confidentialité.</p>
          </div>
        </div>
      </div>
    );
  }

  const totalPrice = calculateTotalPrice();

  return (
    <div className="client-form-container">
      <h2>Vos informations de contact</h2>

      {/* Vue mobile du récapitulatif */}
      <div className="appointment-summary-mobile">
        <h3>Récapitulatif</h3>
        <div className="summary-row">
          <span className="summary-label">Service :</span>
          <span className="summary-value">{bookingData.serviceTitle}</span>
        </div>
        <div className="summary-row">
          <span className="summary-label">Date :</span>
          <span className="summary-value">{bookingData.formattedDate}</span>
        </div>
        <div className="summary-row">
          <span className="summary-label">Heure :</span>
          <span className="summary-value">{bookingData.time}</span>
        </div>
        <div className="summary-row">
          <span className="summary-label">Type :</span>
          <span className="summary-value">
            {isRemoteVisit() && <span className="remote-value">Consultation à distance</span>}
            {isHomeVisit() && <span className="home-value">Visite à domicile</span>}
            {!isRemoteVisit() && !isHomeVisit() && <span>En cabinet</span>}
          </span>
        </div>
        <div className="summary-row">
          <span className="summary-label">Prix :</span>
          <span className="summary-value">
            {totalPrice}€
            {isHomeVisit() && bookingData.homeTransportFee > 0 && (
              <small style={{ display: 'block', color: '#666' }}>
                (dont {bookingData.homeTransportFee}€ de déplacement)
              </small>
            )}
          </span>
        </div>
      </div>

      {/* Information spécifique selon le type de consultation */}
      {isRemoteVisit() && (
        <div className="remote-appointment-info">
          <div className="info-icon">💻</div>
          <div className="info-content">
            <h3>Consultation à distance</h3>
            <p>Vous avez choisi une consultation à distance. Un lien de connexion vous sera envoyé par email avant le rendez-vous.</p>
          </div>
        </div>
      )}

      {isHomeVisit() && (
        <div className="home-appointment-info">
          <div className="info-icon">🏠</div>
          <div className="info-content">
            <h3>Visite à domicile</h3>
            <p>Le praticien se déplacera à votre domicile. Veuillez fournir une adresse précise ci-dessous.</p>
            {bookingData.homeTransportFee > 0 && (
              <p><strong>Frais de déplacement :</strong> {bookingData.homeTransportFee}€</p>
            )}
          </div>
        </div>
      )}

      {globalError && (
        <div className="global-error">
          {globalError}
        </div>
      )}

      <form className="client-form" onSubmit={handleSubmit}>
        <div className={`form-group ${errors.firstname ? 'has-error' : ''}`}>
          <label htmlFor="firstname">Prénom *</label>
          <input
            type="text"
            id="firstname"
            name="firstname"
            value={formData.firstname}
            onChange={handleChange}
            className="form-control"
            placeholder="Votre prénom"
            required
          />
          {errors.firstname && <div className="error-message">{errors.firstname}</div>}
        </div>

        <div className={`form-group ${errors.name ? 'has-error' : ''}`}>
          <label htmlFor="name">Nom *</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="form-control"
            placeholder="Votre nom"
            required
          />
          {errors.name && <div className="error-message">{errors.name}</div>}
        </div>

        <div className={`form-group ${errors.email ? 'has-error' : ''}`}>
          <label htmlFor="email">Email *</label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="form-control"
            placeholder="exemple@email.com"
            required
          />
          {errors.email && <div className="error-message">{errors.email}</div>}
        </div>

        <div className={`form-group ${errors.phone ? 'has-error' : ''}`}>
          <label htmlFor="phone">Téléphone *</label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="form-control"
            placeholder="01 23 45 67 89"
            required
          />
          {errors.phone && <div className="error-message">{errors.phone}</div>}
        </div>

        {/* Section adresse pour les visites à domicile */}
        {isHomeVisit() && (
          <div className="address-section">
            <h4>Adresse de la visite</h4>
            
            <div className={`form-group ${errors['address.street'] ? 'has-error' : ''}`}>
              <label htmlFor="address.street">Adresse *</label>
              <input
                type="text"
                id="address.street"
                name="address.street"
                value={formData.address.street}
                onChange={handleChange}
                className="form-control"
                placeholder="Numéro et nom de rue"
                required
              />
              {errors['address.street'] && <div className="error-message">{errors['address.street']}</div>}
            </div>

            <div className="form-row">
              <div className={`form-group ${errors['address.zipCode'] ? 'has-error' : ''}`}>
                <label htmlFor="address.zipCode">Code postal *</label>
                <input
                  type="text"
                  id="address.zipCode"
                  name="address.zipCode"
                  value={formData.address.zipCode}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="75001"
                  required
                />
                {errors['address.zipCode'] && <div className="error-message">{errors['address.zipCode']}</div>}
              </div>

              <div className={`form-group ${errors['address.city'] ? 'has-error' : ''}`}>
                <label htmlFor="address.city">Ville *</label>
                <input
                  type="text"
                  id="address.city"
                  name="address.city"
                  value={formData.address.city}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Paris"
                  required
                />
                {errors['address.city'] && <div className="error-message">{errors['address.city']}</div>}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="address.additionalInfo">Informations complémentaires</label>
              <input
                type="text"
                id="address.additionalInfo"
                name="address.additionalInfo"
                value={formData.address.additionalInfo}
                onChange={handleChange}
                className="form-control"
                placeholder="Étage, appartement, code d'accès, etc."
              />
            </div>
          </div>
        )}

        <div className="form-group">
          <label htmlFor="notes">Notes (optionnel)</label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            className="form-control"
            placeholder={
              isRemoteVisit() 
                ? "Informations supplémentaires pour le praticien ou préférences pour la consultation à distance" 
                : isHomeVisit()
                ? "Informations supplémentaires pour le praticien ou instructions pour le déplacement"
                : "Informations supplémentaires pour le praticien"
            }
            rows="3"
          ></textarea>
        </div>

        <div className="step-navigation">
          <button type="button" className="btn btn-back" onClick={handleBack}>
            Retour
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Réservation en cours...' : 'Confirmer la réservation'}
          </button>
        </div>
      </form>
    </div>
  );
};

const formatDateForStorage = (date) => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default ClientForm;